import type {
  PortalDelta,
  PortalDeltas,
  PortalLocationRow,
  PortalTotals,
} from '../../types/portalReport.js';
import { computeDelta } from '../../modules/reporting/heroCards';
import { metricDirection, type ChartMetric } from './chartPlan.js';

// CLIENT PORTAL round 2, item 4 — turning the current and comparison totals into
// the percentages shown on each headline figure. PURE, and CLIENT-ONLY.
//
// ⚠️ The split from ./comparison.ts is not tidiness: `computeDelta` lives in the
// staff dashboards' hero-card module, which the server cannot import (it is a
// client module and uses extensionless imports). The WINDOW resolution has to
// run server-side because it decides what to fetch; the PERCENTAGE runs here
// because that is where the shared implementation is. Two halves, one rule each,
// neither re-derived.
//
// ⚠️ `computeDelta` is the SAME function both staff dashboards use, so "+12%"
// means one thing across the whole tool.

/**
 * Build the three deltas.
 *
 * ⚠️ MONEY DELTAS ARE PER CURRENCY, and on a mixed-currency scope there is no
 * single figure to take a percentage of — so spend and CPL get NO delta rather
 * than a blended one, and the reason says why. Leads still compare: a lead is a
 * lead whoever paid. Same refusal the Google dashboard's `googleMetricDelta`
 * makes for the same reason.
 */
export function buildPortalDeltas(
  current: readonly PortalTotals[],
  previous: readonly PortalTotals[],
  unavailableReason: string | null,
): PortalDeltas {
  // ⚠️ An unmeasured comparison period yields NOTHING, never a fabricated
  // −100%. See comparisonUnavailableReason — a client whose backfill starts
  // inside the comparison window would otherwise be told their leads collapsed
  // when the truth is we were not measuring then.
  if (unavailableReason) {
    return { leads: null, spend: null, costPerLead: null, unavailableReason };
  }

  const curLeads = current.reduce((sum, t) => sum + t.leads, 0);
  const prevLeads = previous.reduce((sum, t) => sum + t.leads, 0);
  const leads = toPortalDelta(computeDelta(curLeads, prevLeads));

  if (current.length > 1 || previous.length > 1) {
    return {
      leads,
      spend: null,
      costPerLead: null,
      unavailableReason:
        'This report covers more than one currency, so spend cannot be compared as a single change.',
    };
  }

  const cur = current[0] ?? null;
  const prev = previous[0] ?? null;

  const spend = cur && prev ? toPortalDelta(computeDelta(cur.spend, prev.spend)) : null;
  // ⚠️ A CPL delta needs BOTH sides to exist. A period with no leads has no CPL,
  // and comparing "no CPL" against a real one would manufacture a change out of
  // an undefined figure.
  const costPerLead =
    cur?.costPerLead != null && prev?.costPerLead != null
      ? toPortalDelta(computeDelta(cur.costPerLead, prev.costPerLead))
      : null;

  return { leads, spend, costPerLead, unavailableReason: null };
}

function toPortalDelta(
  delta: { pct: number; direction: 'up' | 'down' | 'flat' } | null,
): PortalDelta | null {
  return delta ? { pct: delta.pct, direction: delta.direction } : null;
}

/**
 * ⚠️ TONE FOLLOWS MEANING, NOT JUST DIRECTION. A falling COST PER LEAD is good
 * news; a falling lead count is not. Mapped to the same `MetricTone` vocabulary
 * the staff dashboards' `deltaColorClass` consumes, so green means the same
 * thing everywhere in the tool.
 */
export function portalMetricTone(
  metric: ChartMetric,
): 'good-up' | 'good-down' | 'neutral' {
  // ⚠️⚠️ DELEGATED, NEVER RESTATED. The chart tooltip's `changeTone` reads the
  // same function, so a falling cost cannot be green on a chart and red in the
  // hero — and adding a metric cannot leave the two disagreeing, because there
  // is only one answer to give.
  return metricDirection(metric);
}

/**
 * One leaderboard row's deltas (§4a, 2026-09-03).
 *
 * ⚠⚠ THE COMPARISON WINDOW IS THE ONE THE CLIENT SELECTED, resolved once
 * server-side and carried on the row — not re-derived here. The leaderboard
 * disagreeing with the hero about "vs what" is worse than having no deltas: a
 * manager reading +12% beside the club rows and −18% above them has no way to
 * tell which is wrong.
 *
 * ⚠⚠ COST PER LEAD'S DELTA COMES FROM THE TWO RE-DERIVED VALUES, never from
 * averaging two cost-per-lead figures. Both sides are already spend ÷ leads for
 * that club and that window; taking a percentage between them is the only honest
 * move, and it is the same rule the creative grouping pass follows.
 *
 * ⚠ A NULL on either side yields NO delta rather than a percentage against
 * nothing. A club with no leads has no cost per lead, and comparing "no cost per
 * lead" to a real one would manufacture a change out of an undefined figure.
 */
export interface PortalLocationDeltas {
  leads: PortalDelta | null;
  spend: PortalDelta | null;
  costPerLead: PortalDelta | null;
  crmLeads: PortalDelta | null;
  crmDigitalLeads: PortalDelta | null;
  crmSales: PortalDelta | null;
}

/**
 * A delta between two bare figures.
 *
 * ⚠️ EXPORTED so the group-average tiles use the SAME `computeDelta` every other
 * figure on this report and both staff dashboards use. A second percentage
 * implementation is a second chance for "+12%" to mean two things in one tool.
 */
export function portalDelta(current: number, previous: number): PortalDelta | null {
  return toPortalDelta(computeDelta(current, previous));
}

export function buildLocationDeltas(row: PortalLocationRow): PortalLocationDeltas {
  const pair = (cur: number | null | undefined, prev: number | null | undefined) =>
    cur != null && prev != null ? toPortalDelta(computeDelta(cur, prev)) : null;

  const p = row.previous;
  const c = row.crm;
  return {
    leads: pair(row.leads, p?.leads),
    spend: pair(row.spend, p?.spend),
    costPerLead: pair(row.costPerLead, p?.costPerLead),
    // ⚠ The CRM's own comparison, which is NOT the ad comparison: the CRM
    // covers a different history and its window is resolved separately, so a
    // club can legitimately show an enquiry change and no spend change.
    crmLeads: pair(c?.leads, c?.previous?.leads),
    crmDigitalLeads: pair(c?.digitalLeads, c?.previous?.digitalLeads),
    crmSales: pair(c?.sales, c?.previous?.sales),
  };
}
